import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Grid } from '@material-ui/core';
import Tooltip from '@material-ui/core/Tooltip';
import moment from 'moment';
import React, { useState } from 'react';
import { Row } from 'react-bootstrap';
import {
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';
import { cnpjMask, cpfMask, rgMask } from '../../services/mask';
import './styles.scss';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { checkAdmin } from '../../services/validation';
import {
  CartStatus,
  PaymentOrderStatus,
  ReceiptStatus,
} from '../../types/drawer.types';
import StatusTag from '../atoms/statusTag';
import Typography from '../atoms/typography';

interface TableProps {
  onViewClick?: (id: any, status?: string) => void;
  onViewHistoric?: (id: any) => void;
  onCheck?: (id: any) => void;
  onCancel?: (id: any) => void;
  onClickPlus?: (id: any) => void;
  onCancelDelivery?: (cartId: string) => void;
  onRetryDelivery?: (cartId: string) => void;
  onReturnedDelivery?: (cartId: string) => void;
  onEditClick?: (id: any) => void;
  onDeleteClick?: (id: any) => void;
  onDocumentClick?: (id: number) => void;
  onStatusClick?: (id: number) => void;
  onRedirectMappingClick?: (id: string) => void;
  onCancelReceipt?: (cartId: string, status?: string) => void;
  onCancelNfe?: (paymentOrderId: string, status?: string) => void;
  onViewDownloadImage?: (id: any) => void;
  onSendNotification?: (id?: any) => void;
  onExpandRows?: (id: string) => void;
  onApprovePaymentOrderGroup?: (paymentOrderGroupId: string) => void;
  columns: Array<any>;
  data: any;
  notShowInput?: boolean;
  disableDelete?: boolean;
}

type TypeValue =
  | 'date'
  | 'price'
  | 'not_view'
  | 'cpf/cnpj'
  | 'kg'
  | 'tn'
  | 'rg'
  | 'yearsOld';

const Table: React.FC<TableProps> = ({
  columns,
  data,
  onDeleteClick,
  onEditClick,
  onViewClick,
  onDocumentClick,
  onStatusClick,
  onRedirectMappingClick,
  onViewHistoric,
  onCheck,
  onCancel,
  onClickPlus,
  onCancelDelivery,
  onReturnedDelivery,
  onRetryDelivery,
  onCancelReceipt,
  onCancelNfe,
  onViewDownloadImage,
  onSendNotification,
  onApprovePaymentOrderGroup,
  onExpandRows,
  notShowInput,
  disableDelete,
}: TableProps) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    setGlobalFilter,
    selectedFlatRows,
    state: { pageIndex, selectedRowIds },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 50 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
  );

  const token = useSelector(
    (state: ApplicationState) => state.auth.token || state.distributor.token,
  );
  const [checked, setChecked] = useState<boolean>(false);
  const [indeterminate, setIndeterminate] = useState<boolean>(false);

  const handleCheckboxChange = (id: any) => {
    if (indeterminate) {
      setIndeterminate(false);
      setChecked(false);
    } else {
      setChecked(!checked);
    }
  };

  const formatRows = (rows: any) => {
    return {
      ...rows,
      id:
        rows.distributorId ||
        rows.distributorUserId ||
        rows.freightId ||
        rows.deliveryId ||
        rows.cartId ||
        rows.driverId ||
        rows.reasonRefusalId ||
        rows.vehicleTypeId,
    };
  };
  const formattingValue = (value: number, type: TypeValue) => {
    switch (type) {
      case 'price':
        if (value) {
          value = Number(value);
          return value?.toLocaleString('pt-br', {
            style: 'currency',
            currency: 'BRL',
            maximumFractionDigits: 2,
          });
        }
        break;

      case 'cpf/cnpj':
        return String(value).length === 11
          ? cpfMask(String(value))
          : String(value).length === 14
            ? cnpjMask(String(value))
            : value;
      case 'kg':
        if (!value) {
          return 'Sem Informação';
        } else {
          return `${value?.toLocaleString('pt-br', {
            unit: 'kilogram',
            style: 'unit',
          })}`;
        }
      case 'tn':
        return `${value?.toLocaleString('pt-br', {
          unit: 'ton',
          style: 'unit',
        })}`;

      case 'date':
        if (!value) {
          return 'Sem Informação';
        } else {
          return `${new Date(value).toLocaleDateString('pt-BR')}`;
        }
      case 'yearsOld':
        if (!value) {
          return 'Sem Informação';
        }
        return moment.duration(moment(new Date()).diff(value)).years();

      case 'rg':
        if (!value) {
          return 'Sem Informação';
        }
        return rgMask(String(value));
      default:
        return value;
    }
  };

  return (
    <div className="container-table">
      {notShowInput}
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup, indexTr) => (
            <tr {...headerGroup.getHeaderGroupProps()} key={indexTr}>
              {headerGroup.headers.map((column: any, indexTh) => (
                <>
                  {column.type !== 'not_view' && (
                    <th {...column.getHeaderProps()} key={indexTh}>
                      <Row noGutters className="header-container">
                        {column.render('Header')}
                      </Row>
                    </th>
                  )}
                </>
              ))}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          {page.length > 0 &&
            page.map((row: any, indexPage) => {
              prepareRow(row);
              row.original = formatRows(row.original);

              return (
                <tr {...row.getRowProps()} key={row.id}>
                  {row.cells.map((cell: any, indexTd: number) => {
                    return (
                      <>
                        {cell.column.type !== 'not_view' && (
                          <>
                            {cell.column.id === 'actions' ? (
                              <td key={indexTd}>
                                <Row noGutters className="collum-actions">
                                  {onStatusClick && (
                                    <button
                                      type="button"
                                      className="btn-empty"
                                      onClick={() =>
                                        onStatusClick(row.original.id)
                                      }
                                    >
                                      <FontAwesomeIcon
                                        icon={solid('check')}
                                        size="1x"
                                        color="#a9a9a9"
                                      />
                                    </button>
                                  )}
                                  {onDocumentClick && (
                                    <button
                                      type="button"
                                      className="btn-empty"
                                      onClick={() =>
                                        onDocumentClick(row.original.id)
                                      }
                                    >
                                      <FontAwesomeIcon
                                        icon={solid('file-lines')}
                                        size="1x"
                                        color="#a9a9a9"
                                      />
                                    </button>
                                  )}
                                  {onViewClick && (
                                    <Tooltip title="Visualizar" placement="top">
                                      <button
                                        type="button"
                                        className="btn-empty"
                                        onClick={() =>
                                          onViewClick(
                                            row.original.id,
                                            row.original.status,
                                          )
                                        }
                                      >
                                        <FontAwesomeIcon
                                          // icon={solid('eye')}
                                          icon={solid('ellipsis-vertical')}
                                          style={{ color: '#ff7605' }}
                                          size="1x"
                                          color="#a9a9a9"
                                        />
                                      </button>
                                    </Tooltip>
                                  )}

                                  {/* {FreightStatus.AWAITING_DRIVER_ASSIGNMENT === row?.original?.freightStatus && onSendNotification && (
                                    <Tooltip title="Enviar notificação" placement="top">
                                      <button
                                        type="button"
                                        className="btn-empty"
                                        onClick={() =>
                                          onSendNotification(row.original.id)
                                        }
                                      >
                                        <FontAwesomeIcon
                                          icon={solid('message')}
                                          size="1x"
                                          color="#a9a9a9"
                                        />
                                      </button>
                                    </Tooltip>
                                  )} */}

                                  {onViewHistoric && (
                                    <Tooltip
                                      title="Visualizar historico"
                                      placement="top"
                                    >
                                      <button
                                        type="button"
                                        className="btn-empty"
                                        onClick={() => {
                                          onViewHistoric(row.original.id);
                                        }}
                                      >
                                        <FontAwesomeIcon
                                          icon={solid('clock-rotate-left')}
                                          size="1x"
                                          color="#a9a9a9"
                                        />
                                      </button>
                                    </Tooltip>
                                  )}

                                  {!checkAdmin(token) &&
                                    CartStatus.DELIVERED ===
                                      row?.original?.status &&
                                    onViewDownloadImage && (
                                      <Tooltip
                                        title="Download Image"
                                        placement="top"
                                      >
                                        <button
                                          type="button"
                                          className="btn-empty"
                                          onClick={() => {
                                            onViewDownloadImage(
                                              row.original.id,
                                            );
                                          }}
                                        >
                                          <FontAwesomeIcon
                                            icon={solid('download')}
                                            size="1x"
                                            color="#a9a9a9"
                                          />
                                        </button>
                                      </Tooltip>
                                    )}

                                  {!checkAdmin(token) &&
                                    [
                                      ReceiptStatus.APPROVED,
                                      ReceiptStatus.WAITING_APPROVAL,
                                    ].includes(row?.original?.receiptStatus) &&
                                    onCancelReceipt && (
                                      <Tooltip
                                        title="Avaliar canhoto"
                                        placement="top"
                                      >
                                        <button
                                          type="button"
                                          className="btn-empty"
                                          onClick={() =>
                                            onCancelReceipt(row.original.id)
                                          }
                                        >
                                          <FontAwesomeIcon
                                            icon={solid('file-excel')}
                                            size="1x"
                                            color="#a9a9a9"
                                          />
                                        </button>
                                      </Tooltip>
                                    )}

                                  {checkAdmin(token) &&
                                    [PaymentOrderStatus.ISSUED].includes(
                                      row?.original?.status,
                                    ) &&
                                    onCancelNfe && (
                                      <Tooltip
                                        title="Avaliar Nfe"
                                        placement="top"
                                      >
                                        <button
                                          type="button"
                                          className="btn-empty"
                                          onClick={() =>
                                            onCancelNfe(
                                              row.original.paymentOrderId,
                                            )
                                          }
                                        >
                                          <FontAwesomeIcon
                                            icon={solid('file-excel')}
                                            size="1x"
                                            color="#a9a9a9"
                                          />
                                        </button>
                                      </Tooltip>
                                    )}
                                  {!checkAdmin(token) &&
                                    !row?.original?.paidAt &&
                                    onApprovePaymentOrderGroup && (
                                      <Tooltip
                                        title="Aprovar pagamento"
                                        placement="top"
                                      >
                                        <button
                                          type="button"
                                          className="btn-empty"
                                          onClick={() =>
                                            onApprovePaymentOrderGroup(
                                              row.original.paymentOrderGroupId,
                                            )
                                          }
                                        >
                                          <FontAwesomeIcon
                                            icon={solid('check')}
                                            size="1x"
                                            color="#a9a9a9"
                                          />
                                        </button>
                                      </Tooltip>
                                    )}
                                  {onCheck && (
                                    <Tooltip
                                      title="Reprocessar"
                                      placement="top"
                                    >
                                      <button
                                        type="button"
                                        className="btn-empty"
                                        onClick={() => {
                                          onCheck(row.original.id);
                                        }}
                                      >
                                        <FontAwesomeIcon
                                          icon={solid('truck-ramp-box')}
                                          size="1x"
                                          color="#a9a9a9"
                                        />
                                      </button>
                                    </Tooltip>
                                  )}

                                  {CartStatus.PROBLEM_REPORTED ===
                                    row?.original?.status &&
                                    onRetryDelivery && (
                                      <Tooltip
                                        title="Tentar entregar novamente"
                                        placement="top"
                                      >
                                        <button
                                          type="button"
                                          className="btn-empty"
                                          onClick={() => {
                                            onRetryDelivery(row.original.id);
                                          }}
                                        >
                                          <FontAwesomeIcon
                                            icon={solid('truck-fast')}
                                            size="1x"
                                            color="#a9a9a9"
                                          />
                                        </button>
                                      </Tooltip>
                                    )}

                                  {CartStatus.PROBLEM_REPORTED ===
                                    row?.original?.status &&
                                    onCancelDelivery && (
                                      <Tooltip
                                        title="Retornar mercadoria para distribuidora"
                                        placement="top"
                                      >
                                        <button
                                          type="button"
                                          className="btn-empty"
                                          onClick={() => {
                                            onCancelDelivery(row.original.id);
                                          }}
                                        >
                                          <FontAwesomeIcon
                                            icon={solid('warehouse')}
                                            size="1x"
                                            color="#a9a9a9"
                                          />
                                        </button>
                                      </Tooltip>
                                    )}
                                  {CartStatus.RETURNING_TO_ORIGIN ===
                                    row?.original?.status &&
                                    onReturnedDelivery && (
                                      <Tooltip
                                        title="Pacote retornado para distribuidora"
                                        placement="top"
                                      >
                                        <button
                                          type="button"
                                          className="btn-empty"
                                          onClick={() => {
                                            onReturnedDelivery(row.original.id);
                                          }}
                                        >
                                          <FontAwesomeIcon
                                            icon={solid('people-carry-box')}
                                            size="1x"
                                            color="#a9a9a9"
                                          />
                                        </button>
                                      </Tooltip>
                                    )}
                                  {onCancel && (
                                    <Tooltip title="Devolver" placement="top">
                                      <button
                                        type="button"
                                        className="btn-empty"
                                        onClick={() => {
                                          onCancel(row.original.id);
                                        }}
                                      >
                                        <FontAwesomeIcon
                                          icon={solid('right-left')}
                                          size="1x"
                                          color="#a9a9a9"
                                        />
                                      </button>
                                    </Tooltip>
                                  )}

                                  {onClickPlus && (
                                    <Tooltip
                                      title={
                                        row.original.isSelected
                                          ? 'Remover um pacote'
                                          : 'Adicionar um pacote'
                                      }
                                      placement="top"
                                    >
                                      <button
                                        type="button"
                                        className="btn-empty"
                                        onClick={() => {
                                          onClickPlus(row.original);
                                        }}
                                      >
                                        <FontAwesomeIcon
                                          icon={
                                            row.original.isSelected
                                              ? solid('minus')
                                              : solid('plus')
                                          }
                                          size="1x"
                                          color="#a9a9a9"
                                        />
                                      </button>
                                    </Tooltip>
                                  )}

                                  {onEditClick && (
                                    <Tooltip title="Editar" placement="top">
                                      <button
                                        type="button"
                                        className="btn-empty"
                                        onClick={() =>
                                          onEditClick(row.original.id)
                                        }
                                      >
                                        <FontAwesomeIcon
                                          icon={solid('pen')}
                                          size="1x"
                                          color="#a9a9a9"
                                        />
                                      </button>
                                    </Tooltip>
                                  )}
                                  {onDeleteClick && (
                                    <Tooltip title="Deletar" placement="top">
                                      <button
                                        type="button"
                                        className="btn-empty"
                                        onClick={() =>
                                          onDeleteClick(row.original.id)
                                        }
                                        disabled={disableDelete}
                                        style={{
                                          opacity: disableDelete ? 0.5 : 1,
                                          cursor: disableDelete
                                            ? 'not-allowed'
                                            : 'pointer',
                                        }}
                                      >
                                        <FontAwesomeIcon
                                          icon={solid('trash-can')}
                                          size="1x"
                                          color="#a9a9a9"
                                        />
                                      </button>
                                    </Tooltip>
                                  )}

                                  {onRedirectMappingClick && (
                                    <Tooltip title="Mapeamento" placement="top">
                                      <button
                                        type="button"
                                        className="btn-empty"
                                        onClick={() =>
                                          onRedirectMappingClick(
                                            row.original.id,
                                          )
                                        }
                                      >
                                        <FontAwesomeIcon
                                          icon={solid('file-excel')}
                                          size="sm"
                                          color="#a9a9a9"
                                        />
                                      </button>
                                    </Tooltip>
                                  )}
                                  {onExpandRows && (
                                    <Tooltip title="Visualizar" placement="top">
                                      <button
                                        type="button"
                                        className="btn-empty"
                                        onClick={() =>
                                          onExpandRows(row.original.id)
                                        }
                                      >
                                        <FontAwesomeIcon
                                          icon={solid('eye')}
                                          size="sm"
                                          color="#a9a9a9"
                                        />
                                      </button>
                                    </Tooltip>
                                  )}
                                </Row>
                              </td>
                            ) : (
                              <td {...cell.getCellProps()} key={indexTd}>
                                {cell.column.id === 'freightStatus' ? (
                                  <StatusTag status={cell.value} />
                                ) : cell.column.type !== undefined ? (
                                  <Tooltip title={cell.value} placement="top">
                                    <span>
                                      {formattingValue(
                                        cell.value,
                                        cell.column.type,
                                      )}
                                    </span>
                                  </Tooltip>
                                ) : (
                                  cell.value
                                )}
                              </td>
                            )}
                          </>
                        )}
                      </>
                    );
                  })}
                </tr>
              );
            })}
        </tbody>
      </table>
      {!page.length && (
        <Grid container>
          <Grid
            item
            style={{
              paddingTop: 10,
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <Typography fontWeight="400" fontSize="default" color="secondary">
              Sem informação
            </Typography>
          </Grid>
        </Grid>
      )}
    </div>
  );
};

export default Table;

